import { useFeatureFlagEnabled } from '@posthog/react';
import { useSnapshot } from 'valtio';
import { posthogFlagsStore } from '~/common/posthog';
import rootStore from '~/store';

/**
 * A PostHog flag with its **resolution** state, for gates that must not act before the
 * flags response for the *currently active* organization has landed.
 *
 * `resolved` is **per-org**: true only when a flags response has landed for the active
 * organization group (`posthogFlagsStore.flagsResolvedForOrgId`, maintained in
 * `common/posthog.ts`). Flag values are only meaningful for the org whose group was active when
 * they were fetched — a global "flags have loaded" signal goes true on the pre-`group()` initial
 * load and stays true across org switches, both windows where `enabled` is stale and acting on
 * it fires org-scoped calls the server 403s (or runs the wrong code path for a flag-ON org).
 *
 * It must also stay flag-AGNOSTIC, NOT `useFeatureFlagEnabled(...) !== undefined`: PostHog
 * **omits a disabled flag from the flags payload entirely**, so `useFeatureFlagEnabled` stays
 * `undefined` for a flag-OFF user even after flags load — keying "resolved" off it would strand
 * those users on the loader forever. `enabled` comes from the per-flag hook (absent/false →
 * `false`); it may lag during a switch, which is exactly what `resolved === false` masks.
 */
export function useOrgResolvedFlag(flagKey: string): { resolved: boolean; enabled: boolean } {
  const { flagsResolvedForOrgId } = useSnapshot(posthogFlagsStore);
  const { currentOrganizationId } = useSnapshot(rootStore) as typeof rootStore;
  const resolved = flagsResolvedForOrgId !== null && flagsResolvedForOrgId === currentOrganizationId;
  const enabled = useFeatureFlagEnabled(flagKey) === true;
  return { resolved, enabled };
}
